import {
  Alert,
  Box,
  Button,
  Chip,
  Radio,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { UsagePlan } from "../services/usage-plan-service";
import { getFullAccessPlans, getInferenceOnlyPlans } from "../services/usage-plan-service";
import { formatCurrency } from "../pages/UsageAndCost/utils";

export type SubscriptionPlanId = "inference_only" | "full_access";

export type SubscriptionSelection = {
  planId: SubscriptionPlanId | null;
  selectedPlanCode: string | null;
};

type SubscriptionPlanSelectorProps = {
  value: SubscriptionSelection;
  onChange: (value: SubscriptionSelection) => void;
  disabled?: boolean;
  usagePlans: UsagePlan[];
  plansLoading?: boolean;
  plansError?: boolean;
  onRetryPlans?: () => void;
};

type BillingPeriod = "monthly" | "yearly";

const getBillingPeriod = (commitmentTerm: string): BillingPeriod => {
  const normalizedTerm = commitmentTerm.trim().toUpperCase();
  return normalizedTerm === "ANNUAL" ? "yearly" : "monthly";
};

const SubscriptionPlanSelector = ({
  value,
  onChange,
  disabled = false,
  usagePlans,
  plansLoading = false,
  plansError = false,
  onRetryPlans,
}: SubscriptionPlanSelectorProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const fieldBorderColor = isDarkMode ? "rgba(148, 163, 184, 0.24)" : "#D8E2DE";
  const cardBackgroundColor = isDarkMode ? "#081111" : "#FFFFFF";
  const selectedBorderColor = isDarkMode
    ? "#E2E8F0"
    : theme.palette.primary.main;
  const mutedTextColor = isDarkMode ? "#B7C8C1" : "#52635E";

  const inferenceOnlyPlans = useMemo(
    () => getInferenceOnlyPlans(usagePlans),
    [usagePlans],
  );
  const fullAccessPlans = useMemo(
    () => getFullAccessPlans(usagePlans),
    [usagePlans],
  );
  const selectedPlanCode = value.selectedPlanCode ?? null;
  const selectedInferenceOnlyPlan = inferenceOnlyPlans.find(
    (plan) => plan.code === selectedPlanCode,
  );
  const selectedFullAccessPlan = fullAccessPlans.find(
    (plan) => plan.code === selectedPlanCode,
  );
  const displayedInferenceOnlyPlan = selectedInferenceOnlyPlan ?? null;
  const expandedPackagePlans =
    value.planId === "full_access"
      ? fullAccessPlans
      : value.planId === "inference_only"
        ? inferenceOnlyPlans
        : [];
  const expandedPlanId =
    value.planId === "inference_only" ? "inference_only" : "full_access";

  const getPlanPriceLabel = (plan: UsagePlan) => {
    if (plan.price === 0) return t("subscriptionPlans.free");

    const suffix =
      getBillingPeriod(plan.commitment_term) === "yearly"
        ? t("subscriptionPlans.perYear")
        : t("subscriptionPlans.perMonth");
    return `${formatCurrency(plan.price)}/${suffix}`;
  };

  const dynamicCardSx = (selected: boolean, cardDisabled = false) => ({
      width: "100%",
      minHeight: 174,
      p: 2,
      textAlign: "left",
      borderRadius: "8px",
      border: "1px solid",
      borderColor: selected ? selectedBorderColor : fieldBorderColor,
      bgcolor: cardBackgroundColor,
      color: theme.palette.text.primary,
      cursor: cardDisabled ? "default" : "pointer",
      font: "inherit",
      opacity: cardDisabled ? 0.55 : 1,
      transition: "border-color 0.2s ease, background-color 0.2s ease",
    });

    const handleSelectInferenceOnly = () => {
      if (!inferenceOnlyPlans.length || disabled) return;
      onChange({
        ...value,
        planId: "inference_only",
        selectedPlanCode:
          value.planId === "inference_only" ? value.selectedPlanCode : null,
      });
    };

    const handleSelectFullAccess = () => {
      if (!fullAccessPlans.length || disabled) return;
      onChange({
        ...value,
        planId: "full_access",
        selectedPlanCode:
          value.planId === "full_access" ? value.selectedPlanCode : null,
      });
    };

    const handleSelectPackage = (
      planCode: string,
      planId: SubscriptionPlanId,
    ) => {
      if (disabled) return;
      onChange({ ...value, planId, selectedPlanCode: planCode });
    };

    if (plansLoading) {
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 1.5,
          }}
        >
          {[0, 1].map((item) => (
            <Skeleton
              key={item}
              variant="rounded"
              height={174}
              sx={{ borderRadius: "8px" }}
            />
          ))}
        </Box>
      );
    }

    if (plansError) {
      return (
        <Alert
          severity="error"
          action={
            onRetryPlans ? (
              <Button color="inherit" size="small" onClick={onRetryPlans}>
                {t("common.retry")}
              </Button>
            ) : null
          }
        >
          {t("subscriptionPlans.loadError")}
        </Alert>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 1.5,
          }}
        >
          <Box
            component="button"
            type="button"
            disabled={disabled || !inferenceOnlyPlans.length}
            onClick={handleSelectInferenceOnly}
            sx={dynamicCardSx(
              value.planId === "inference_only",
              disabled || !inferenceOnlyPlans.length,
            )}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                {t("subscriptionPlans.inferenceOnly.name")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {displayedInferenceOnlyPlan && (
                  <Chip
                    size="small"
                    label={getPlanPriceLabel(displayedInferenceOnlyPlan)}
                    sx={{
                      height: 26,
                      borderRadius: "8px",
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      fontWeight: 700,
                    }}
                  />
                )}
                <Radio
                  checked={value.planId === "inference_only"}
                  disabled={disabled || !inferenceOnlyPlans.length}
                  size="small"
                  sx={{ p: 0 }}
                />
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: mutedTextColor, mt: 1 }}>
              {t("subscriptionPlans.inferenceOnly.description")}
            </Typography>
            {!inferenceOnlyPlans.length && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("subscriptionPlans.inferenceUnavailable")}
              </Typography>
            )}
          </Box>

          <Box
            component="button"
            type="button"
            disabled={disabled || !fullAccessPlans.length}
            onClick={handleSelectFullAccess}
            sx={dynamicCardSx(
              value.planId === "full_access",
              disabled || !fullAccessPlans.length,
            )}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                {t("subscriptionPlans.fullAccess.name")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {selectedFullAccessPlan && (
                  <Chip
                    size="small"
                    label={getPlanPriceLabel(selectedFullAccessPlan)}
                    sx={{
                      height: 26,
                      borderRadius: "8px",
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      fontWeight: 700,
                    }}
                  />
                )}
                <Radio
                  checked={value.planId === "full_access"}
                  disabled={disabled || !fullAccessPlans.length}
                  size="small"
                  sx={{ p: 0 }}
                />
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: mutedTextColor, mt: 1 }}>
              {t("subscriptionPlans.fullAccess.description")}
            </Typography>
            {!fullAccessPlans.length && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("subscriptionPlans.fullAccessUnavailable")}
              </Typography>
            )}
          </Box>
        </Box>

        {expandedPackagePlans.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {t(
                expandedPlanId === "inference_only"
                  ? "subscriptionPlans.selectInferencePackage"
                  : "subscriptionPlans.selectPackage",
              )}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "stretch",
                gap: 1.5,
                overflowX: "auto",
                pb: 1.25,
                scrollSnapType: "x proximity",
                scrollbarWidth: "thin",
                scrollbarColor: `${theme.palette.primary.main} ${
                  isDarkMode ? "#101D1A" : "#E8F1ED"
                }`,
                "&::-webkit-scrollbar": { height: 10 },
                "&::-webkit-scrollbar-track": {
                  bgcolor: isDarkMode ? "#101D1A" : "#E8F1ED",
                  borderRadius: 999,
                },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: theme.palette.primary.main,
                  border: "2px solid transparent",
                  backgroundClip: "content-box",
                  borderRadius: 999,
                  "&:hover": {
                    bgcolor: isDarkMode
                      ? theme.palette.primary.light
                      : theme.palette.primary.dark,
                  },
                },
              }}
            >
              {expandedPackagePlans.map((plan) => {
                const selected = selectedPlanCode === plan.code;
                const periodSuffix =
                  getBillingPeriod(plan.commitment_term) === "yearly"
                    ? t("subscriptionPlans.perYear")
                    : t("subscriptionPlans.perMonth");

                return (
                  <Box
                    key={plan.code}
                    sx={{
                      flex: "0 0 clamp(260px, 42vw, 330px)",
                      minHeight: 340,
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      scrollSnapAlign: "start",
                      border: "1px solid",
                      borderColor: selected
                        ? selectedBorderColor
                        : fieldBorderColor,
                      borderRadius: "8px",
                      bgcolor: cardBackgroundColor,
                      color: theme.palette.text.primary,
                      opacity: disabled ? 0.7 : 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>
                        {plan.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${formatCurrency(plan.price)}/${periodSuffix}`}
                        sx={{
                          flexShrink: 0,
                          height: 24,
                          borderRadius: "7px",
                          bgcolor: isDarkMode
                            ? "rgba(61, 112, 92, 0.55)"
                            : "#DCEDE6",
                          color: theme.palette.text.primary,
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: mutedTextColor, mt: 1 }}>
                      {plan.description}
                    </Typography>
                    {Array.isArray(plan.included_feature) &&
                      plan.included_feature.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="caption" fontWeight={700}>
                        {t("subscriptionPlans.whatsIncluded")}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mt: 1.25 }}>
                        {plan.included_feature.map((feature) => (
                          <Box
                            key={feature}
                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                          >
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                display: "grid",
                                placeItems: "center",
                                border: "1px solid",
                                borderColor: fieldBorderColor,
                                borderRadius: "5px",
                              }}
                            >
                              <CheckRoundedIcon sx={{ fontSize: 13 }} />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    )}
                    <Box sx={{ flexGrow: 1, minHeight: 24 }} />
                    <Button
                      type="button"
                      fullWidth
                      variant={selected ? "contained" : "outlined"}
                      disabled={disabled}
                      onClick={() => handleSelectPackage(plan.code, expandedPlanId)}
                      sx={{ mt: 2, textTransform: "none" }}
                    >
                      {selected
                        ? t("subscriptionPlans.selected")
                        : t("subscriptionPlans.selectPlan")}
                    </Button>
                  </Box>
                );
              })}
            </Box>

            {!expandedPackagePlans.length && (
              <Alert severity="info">
                {t("subscriptionPlans.noPackagesForPeriod")}
              </Alert>
            )}
          </Box>
        )}
      </Box>
    );
};

export default SubscriptionPlanSelector;
